import { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  minHeight?: string
  debounceMs?: number
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Scrivi qui...',
  disabled = false,
  minHeight = '200px',
  debounceMs = 500
}: RichTextEditorProps) {
  // Stato locale per editing immediato senza flickering
  const [localValue, setLocalValue] = useState(value || '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isInternalChange = useRef(false)

  // Sincronizza valore esterno -> locale (solo se non stiamo editando)
  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(value || '')
    }
    isInternalChange.current = false
  }, [value])

  const handleChange = useCallback((content: string) => {
    // Normalizza contenuto vuoto
    const normalizedContent = (content === '<p><br></p>' || content === '<p></p>') ? '' : content

    // Aggiorna stato locale immediatamente (no flickering)
    setLocalValue(normalizedContent)
    isInternalChange.current = true

    // Debounce la chiamata al parent
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      onChange(normalizedContent)
    }, debounceMs)
  }, [onChange, debounceMs])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
    },
    clipboard: {
      matchVisual: false,
    },
  }), [])

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image'
  ]

  return (
    <div className={`rich-text-editor ${disabled ? 'opacity-60' : ''}`}>
      <style>{`
        .rich-text-editor .ql-container {
          min-height: ${minHeight};
          font-size: 14px;
        }
        .rich-text-editor .ql-editor {
          min-height: ${minHeight};
        }
        .rich-text-editor .ql-toolbar {
          background: #f9fafb;
          border-color: #e5e7eb;
          border-radius: 8px 8px 0 0;
        }
        .rich-text-editor .ql-container {
          border-color: #e5e7eb;
          border-radius: 0 0 8px 8px;
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .rich-text-editor .ql-editor img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
      <ReactQuill
        theme="snow"
        value={localValue}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </div>
  )
}
